/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useRef, useCallback} from 'react'
import './InventoryVisualizerWrapper.css'
/**
 * Single-file React+TS example:
 * - No browser scrolling (body overflow hidden).
 * - Map fills 100% viewport (width/height).
 * - Pan/zoom within the map using transform: translate + scale.
 * - Based on your final code with multi-column sub-locations, no extra lines,
 *   inbound/outbound widened, etc.
 */

// ---------------------------------------------------------------------
// 1. Data Interfaces (same as your snippet)
// ---------------------------------------------------------------------

interface SubLocation {
  name: string
  qty?: number
  val?: number
}

type NodeType = 'inbound' | 'outbound' | 'warehouse' | '3pl' | 'store' | 'amazon'

interface MapNode {
  id: string
  nodeType: NodeType
  title: string
  top: number
  left: number
  totalQty?: number
  totalVal?: number
  subLocations: SubLocation[]
  width?: number
  height?: number
}

interface MapArrow {
  fromId: string
  toId: string
}

// ---------------------------------------------------------------------
// 2. Our Node Data (no extra lines, final arrangement)
// ---------------------------------------------------------------------

let nodeData: MapNode[] = [
  {
    id: 'vendorInbound',
    nodeType: 'inbound',
    title: 'Inbound (Vendor)',
    top: 200,
    left: 80,
    totalQty: 400,
    totalVal: 12000,
    subLocations: [
      {name: 'From Supplier A', qty: 150, val: 4500},
      {name: 'From Supplier B', qty: 250, val: 7500},
    ],
  },
  {
    id: 'amazonInbound',
    nodeType: 'inbound',
    title: 'Inbound (Amazon Returns)',
    top: 60,
    left: 480,
    totalQty: 80,
    totalVal: 2400,
    subLocations: [
      {name: 'Cust Damaged', qty: 50, val: 1500},
      {name: 'Warehouse Damaged', qty: 30, val: 900},
    ],
  },
  {
    id: 'mainWarehouse',
    nodeType: 'warehouse',
    title: 'Main Warehouse',
    top: 300,
    left: 520,
    totalQty: 5000,
    totalVal: 150000,
    subLocations: [
      {name: 'Pickable', qty: 1200, val: 36000},
      {name: 'Reserve', qty: 2500, val: 75000},
      {name: 'Damaged', qty: 30, val: 900},
    ],
  },
  {
    id: 'outboundStore',
    nodeType: 'outbound',
    title: 'Outbound → Store',
    top: 600,
    left: 950,
    totalQty: 100,
    totalVal: 3000,
    subLocations: [
      {name: 'Shipment #1', qty: 40, val: 1200},
      {name: 'Shipment #2', qty: 60, val: 1800},
    ],
  },
  {
    id: 'outboundVendor',
    nodeType: 'outbound',
    title: 'Outbound → Vendor',
    top: 440,
    left: 950,
    totalQty: 50,
    totalVal: 1500,
    subLocations: [{name: 'Return #1', qty: 50, val: 1500}],
  },
  {
    id: 'outboundAmazon',
    nodeType: 'outbound',
    title: 'Outbound → Amazon',
    top: 280,
    left: 950,
    totalQty: 150,
    totalVal: 4500,
    subLocations: [
      {name: 'Shipment #A', qty: 70, val: 2100},
      {name: 'Shipment #B', qty: 80, val: 2400},
    ],
  },
  {
    id: 'amazonFBA',
    nodeType: 'amazon',
    title: 'Amazon FBA',
    top: 40,
    left: 900,
    totalQty: 3400,
    totalVal: 102000,
    subLocations: [
      {name: 'Fulfillable', qty: 2000, val: 60000},
      {name: 'Reserved', qty: 700, val: 21000},
      {name: 'InboundWk', qty: 200, val: 6000},
      {name: 'Unfulfillable', qty: 50, val: 1500},
    ],
  },
  {
    id: 'threePL',
    nodeType: '3pl',
    title: '3rd Party (3PL)',
    top: 580,
    left: 220,
    totalQty: 900,
    totalVal: 27000,
    subLocations: [
      {name: 'easy3pl', qty: 500, val: 15000},
      {name: '2d workshop', qty: 400, val: 12000},
    ],
  },
  {
    id: 'retailStores',
    nodeType: 'store',
    title: 'Retail Stores (11)',
    top: 600,
    left: 520,
    totalQty: 2200,
    totalVal: 88000,
    subLocations: [
      {name: 'Store #R001', qty: 200, val: 8000},
      {name: 'Store #R002', qty: 180, val: 7200},
      {name: 'Store #R003', qty: 250, val: 10000},
      {name: 'Store #R004', qty: 150, val: 6000},
      {name: 'Store #R005', qty: 200, val: 8000},
      {name: 'Store #R006', qty: 200, val: 8000},
      {name: 'Store #R007', qty: 250, val: 10000},
      {name: 'Store #R008', qty: 120, val: 4800},
      {name: 'Store #R009', qty: 300, val: 12000},
      {name: 'Store #R010', qty: 150, val: 6000},
      {name: 'Store #R011', qty: 150, val: 6000},
    ],
  },
]

const arrowData: MapArrow[] = [
  {fromId: 'vendorInbound', toId: 'mainWarehouse'},
  {fromId: 'amazonInbound', toId: 'mainWarehouse'},
  {fromId: 'mainWarehouse', toId: 'outboundStore'},
  {fromId: 'mainWarehouse', toId: 'outboundVendor'},
  {fromId: 'mainWarehouse', toId: 'outboundAmazon'},
  {fromId: 'outboundStore', toId: 'retailStores'},
  {fromId: 'outboundAmazon', toId: 'amazonFBA'},
  {fromId: 'mainWarehouse', toId: 'threePL'},
]

// ---------------------------------------------------------------------
// 3. The main Page
// ---------------------------------------------------------------------
export const InventoryVisualizerPage: FC = () => {
  // 1) compute node widths/heights for sub-locations
  nodeData = nodeData.map((n) => {
    const {width, height} = computeNodeSize(n)
    return {...n, width, height}
  })

  // 2) pan/zoom state
  const [scale, setScale] = useState(1)
  const [translate, setTranslate] = useState({x: 0, y: 0})
  const [dragging, setDragging] = useState(false)
  const [startPos, setStartPos] = useState({x: 0, y: 0})

  // min/max scale
  const MIN_SCALE = 0.4
  const MAX_SCALE = 2.5

  // pointer events
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    setDragging(true)
    setStartPos({x: e.clientX - translate.x, y: e.clientY - translate.y})
  }
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return
    const newX = e.clientX - startPos.x
    const newY = e.clientY - startPos.y
    setTranslate({x: newX, y: newY})
  }
  const handlePointerUp = () => {
    setDragging(false)
  }

  // wheel zoom
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    let newScale = scale - e.deltaY * 0.001
    if (newScale < MIN_SCALE) newScale = MIN_SCALE
    if (newScale > MAX_SCALE) newScale = MAX_SCALE
    setScale(newScale)
  }

  // 3) style for the transformable "map"
  const mapStyle: React.CSSProperties = {
    width: '3000px',
    height: '2000px',
    transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
    transformOrigin: '0 0',
    cursor: dragging ? 'grabbing' : 'grab',
    position: 'relative',
  }

  return (
    <div style={pageStyle}>
      <h2 style={pageTitleStyle}>Inventory Map - Proof of Concept - DEMO</h2>
      {/* No scroll outside, fully occupying viewport: */}
      <div
        style={fullscreenContainer}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        onWheel={handleWheel}
      >
        <div style={mapStyle}>
          <div style={arrowLayerStyle}>
            {arrowData.map((arrow, idx) => {
              const fromNode = nodeData.find((x) => x.id === arrow.fromId)
              const toNode = nodeData.find((x) => x.id === arrow.toId)
              if (!fromNode || !toNode) return null
              const fromX = fromNode.left + (fromNode.width ?? 0) / 2
              const fromY = fromNode.top + (fromNode.height ?? 0) / 2
              const toX = toNode.left + (toNode.width ?? 0) / 2
              const toY = toNode.top + (toNode.height ?? 0) / 2
              return <ArrowLine key={idx} fromX={fromX} fromY={fromY} toX={toX} toY={toY} />
            })}
          </div>
          <div style={nodeLayerStyle}>
            {nodeData.map((node) => (
              <NodeBox key={node.id} node={node} />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// The wrapper you requested
export const InventoryVisualizerWrapper: FC = () => {
  return (
    <>
      <InventoryVisualizerPage />
    </>
  )
}

// ---------------------------------------------------------------------
// 4. NodeBox + ArrowLine
// ---------------------------------------------------------------------
interface NodeBoxProps {
  node: MapNode
}
const NodeBox: React.FC<NodeBoxProps> = ({node}) => {
  const {width = 200, height = 200} = node
  const boxStyle: React.CSSProperties = {
    position: 'absolute',
    top: node.top,
    left: node.left,
    width,
    height,
    backgroundColor: colorForType(node.nodeType),
    borderRadius: 8,
    border: '1px solid #666',
    padding: 8,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
  }

  return (
    <div style={boxStyle}>
      {/* header row */}
      <div style={nodeHeaderRow}>
        <div style={{display: 'flex', alignItems: 'center'}}>
          <span style={iconStyle}>{iconForType(node.nodeType)}</span>
          <span style={nodeTitleStyle}>{node.title}</span>
        </div>
        <div style={{fontSize: '0.8rem', color: '#ccc'}}>
          {node.totalQty !== undefined && (
            <span style={{marginRight: '1rem'}}>Q: {node.totalQty}</span>
          )}
          {node.totalVal !== undefined && <span>Val: ${node.totalVal.toLocaleString()}</span>}
        </div>
      </div>
      <div style={getSubLocGridStyle(node)}>
        {node.subLocations.map((sub, i) => (
          <div key={i} style={subSquareStyle}>
            <div style={subNameStyle}>{sub.name}</div>
            {sub.qty !== undefined && <div style={subLineStyle}>Q: {sub.qty}</div>}
            {sub.val !== undefined && <div style={subLineStyle}>$: {sub.val.toLocaleString()}</div>}
          </div>
        ))}
      </div>
    </div>
  )
}

interface ArrowProps {
  fromX: number
  fromY: number
  toX: number
  toY: number
}
const ArrowLine: React.FC<ArrowProps> = ({fromX, fromY, toX, toY}) => {
  const dx = toX - fromX
  const dy = toY - fromY
  const dist = Math.sqrt(dx * dx + dy * dy)
  const angle = (Math.atan2(dy, dx) * 180) / Math.PI

  return (
    <div
      style={{
        position: 'absolute',
        top: fromY,
        left: fromX,
        width: dist,
        height: 2,
        backgroundColor: '#aaa',
        transformOrigin: '0 50%',
        transform: `rotate(${angle}deg)`,
      }}
    >
      {/* arrowhead */}
      <div
        style={{
          position: 'absolute',
          top: -4,
          right: 0,
          width: 0,
          height: 0,
          borderLeft: '6px solid #aaa',
          borderTop: '4px solid transparent',
          borderBottom: '4px solid transparent',
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------
// 5. Auto-size logic
// ---------------------------------------------------------------------
const SUB_SIZE = 60
const GRID_GAP = 8

function computeNodeSize(node: MapNode): {width: number; height: number} {
  const sc = node.subLocations.length
  const cols = columnsForCount(sc)
  const rows = Math.ceil(sc / cols)
  const subW = cols * SUB_SIZE + (cols - 1) * GRID_GAP
  const subH = rows * SUB_SIZE + (rows - 1) * GRID_GAP
  const headerH = 60
  let totalW = Math.max(240, subW + 16)
  let totalH = headerH + subH + 16

  if (node.nodeType === 'inbound' || node.nodeType === 'outbound') {
    totalW = Math.max(totalW, 300)
  }
  if (node.nodeType === 'warehouse') {
    totalW = Math.max(totalW, 400)
    totalH = Math.max(totalH, 250)
  }
  if (node.nodeType === 'store') {
    totalW = Math.max(totalW, 350)
    totalH = Math.max(totalH, 350)
  }
  return {width: totalW, height: totalH}
}
function columnsForCount(sc: number): number {
  if (sc <= 2) return 2
  if (sc <= 6) return 3
  return 4
}
function getSubLocGridStyle(node: MapNode): React.CSSProperties {
  const sc = node.subLocations.length
  const cols = columnsForCount(sc)
  return {
    display: 'grid',
    gridGap: `${GRID_GAP}px`,
    gridTemplateColumns: `repeat(${cols}, ${SUB_SIZE}px)`,
    flex: 1,
    alignContent: 'flex-start',
  }
}

// ---------------------------------------------------------------------
// 6. Colors & Icons
// ---------------------------------------------------------------------
function colorForType(t: NodeType) {
  switch (t) {
    case 'inbound':
      return '#2d6a4f'
    case 'outbound':
      return '#c77d36'
    case 'warehouse':
      return '#3a3a3a'
    case '3pl':
      return '#495057'
    case 'store':
      return '#7b3c84'
    case 'amazon':
      return '#5c636a'
    default:
      return '#444'
  }
}
function iconForType(t: NodeType) {
  switch (t) {
    case 'inbound':
      return '🚚'
    case 'outbound':
      return '📦'
    case 'warehouse':
      return '🏭'
    case '3pl':
      return '⚙️'
    case 'store':
      return '🏬'
    case 'amazon':
      return '🅰️'
    default:
      return '📁'
  }
}

// ---------------------------------------------------------------------
// 7. Style objects
// ---------------------------------------------------------------------
const pageStyle: React.CSSProperties = {
  // Remove page scroll by forcing no overflow
  width: '100%',
  height: '750px',
  overflow: 'hidden',
  backgroundColor: '#1c1c1c',
  color: '#fff',
  margin: 0,
  padding: 0,
  fontFamily: 'sans-serif',
  display: 'flex',
  flexDirection: 'column',
}

const pageTitleStyle: React.CSSProperties = {
  textAlign: 'center',
  margin: '0 0 1rem 0',
  paddingTop: '1rem',
  fontSize: '1.2rem',
}

// The container that captures pointer events for panning & wheel for zoom
const fullscreenContainer: React.CSSProperties = {
  flex: 1,
  overflow: 'hidden', // no scroll
  userSelect: 'none',
  touchAction: 'none', // allow pointer events
  position: 'relative',
}

// The arrow/layer areas are 3000x2000 but transformed
const arrowLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '3000px',
  height: '2000px',
  zIndex: 1,
}
const nodeLayerStyle: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '3000px',
  height: '2000px',
  zIndex: 2,
}

// Node header
const nodeHeaderRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px solid #555',
  paddingBottom: 4,
  marginBottom: 4,
}
const iconStyle: React.CSSProperties = {
  fontSize: '1.2rem',
  marginRight: 6,
}
const nodeTitleStyle: React.CSSProperties = {
  fontWeight: 600,
  fontSize: '1rem',
}

// sub-loc squares
const subSquareStyle: React.CSSProperties = {
  width: SUB_SIZE,
  height: SUB_SIZE,
  backgroundColor: 'rgba(255,255,255,0.15)',
  borderRadius: 4,
  padding: '2px 4px',
  boxSizing: 'border-box' as const,
  fontSize: '0.7rem',
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  overflow: 'hidden',
}
const subNameStyle: React.CSSProperties = {
  fontWeight: 600,
  marginBottom: 2,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}
const subLineStyle: React.CSSProperties = {
  lineHeight: 1,
}
